import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Container, Paper, Snackbar, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../../contexts/AuthContext';
import OrgManagement from '../Profile/components/OrgManagement';

const OrganisationPage = () => {
  const { org_id: org_id_param } = useParams();
  const org_id = parseInt(org_id_param, 10);  // params are strings; JWT carries int
  const navigate = useNavigate();
  const { activeOrg, orgRole, switchToOrg, isOrgContext } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [switchErr, setSwitchErr] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const isActiveOrg = isOrgContext && activeOrg?.org_id === org_id;

  const handleSwitch = async () => {
    setSwitching(true);
    setSwitchErr('');
    try {
      await switchToOrg(org_id);
    } catch (err) {
      setSwitchErr(err.message || 'Failed to switch context. Make sure you are a member of this organisation.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
      <Container maxWidth="lg" sx={{ pt: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back
        </Button>

        {!isActiveOrg ? (
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center', maxWidth: 480, mx: 'auto', mt: 6 }}>
            <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>Organisation Dashboard</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Switch to this organisation context to view its dashboard, members, events, and audit log.
            </Typography>
            {switchErr && <Alert severity="error" sx={{ mb: 2 }}>{switchErr}</Alert>}
            <Button
              variant="contained"
              size="large"
              onClick={handleSwitch}
              disabled={switching}
              startIcon={switching ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Switch to Organisation
            </Button>
          </Paper>
        ) : (
          <>
            <Typography variant="h5" fontWeight={700} mb={3}>
              {activeOrg?.org_name}
            </Typography>
            <OrgManagement orgId={org_id} orgRole={orgRole} onSnack={showSnack} />
          </>
        )}
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrganisationPage;
